import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../../axiosinterceptor";

const initialFilterPayload = {
  speciality: [],
  coder: "",
  principal_diagnosis: [],
  drg: [],
  health_fund: [],
  status: [],
  admission_date_from: "",
  admission_date_to: "",
  separation_date_from: "",
  separation_date_to: "",
  episode_id: [],
  audit_rule_category: [],
  los_op: "",
  los: "",
  file_name: [],
  financial_impact: "",
};
const nowIso = () => new Date().toISOString();
export const fetchWorkflowDashboard = createAsyncThunk(
  "workflowDashboard/fetch",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/code_enhance_dashboard_my_dashboard",
        payload,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to load dashboard data",
      );
    }
  },
);
export const fetchCoderAccuracy = createAsyncThunk(
  "workflowDashboard/fetchCoderAccuracy",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/code_enhance_dashboard_coder_accuracy",
        payload,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to load coder accuracy",
      );
    }
  },
);
export const fetchCodeChanges = createAsyncThunk(
  "workflowDashboard/fetchCodeChanges",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/code_enhance_dashboard_code_changes",
        payload,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to load code changes",
      );
    }
  },
);
export const fetchTopDrg = createAsyncThunk(
  "workflowDashboard/fetchTopDrg",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/code_enhance_dashboard_top10_drg_changes",
        payload,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to load top DRG changes",
      );
    }
  },
);
export const fetchSpecialtyAuditOutcomes = createAsyncThunk(
  "workflowDashboard/fetchSpecialtyAuditOutcomes",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/code_enhance_dashboard_speciality_audit_outcomes",
        payload,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          "Failed to load speciality audit outcomes",
      );
    }
  },
);
export const fetchFinancialImpact = createAsyncThunk(
  "workflowDashboard/fetchFinancialImpact",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/code_enhance_dashboard_financial_impact",
        payload,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to load financial impact",
      );
    }
  },
);
export const toggleTilePin = createAsyncThunk(
  "workflowDashboard/toggleTilePin",
  async ({ tileName, pin }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/code_enhance_dashboard_personalized_dashboard",
        { tile_name: [tileName], pin }, // wrap in array
      );
      return { tileName, pin, message: response.data?.message };
    } catch (error) {
      return rejectWithValue({
        tileName,
        pin,
        message: error?.response?.data?.message || "Failed to update pin",
      });
    }
  },
);
export const fetchCoderPerformanceMetrics = createAsyncThunk(
  "workflowDashboard/fetchCoderPerformanceMetrics",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/code_enhance_dashboard_coder_performance_metrics",
        payload,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          "Failed to load coder performance metrics",
      );
    }
  },
);
export const fetchDiagnosisChangesTimeline = createAsyncThunk(
  "workflowDashboard/fetchDiagnosisChangesTimeline",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/code_enhance_dashboard_diagnosis_changes",
        payload,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          "Failed to load diagnosis changes timeline",
      );
    }
  },
);

export const fetchProcedureChangesTimeline = createAsyncThunk(
  "workflowDashboard/fetchProcedureChangesTimeline",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/code_enhance_dashboard_procedure_changes",
        payload,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          "Failed to load procedure changes timeline",
      );
    }
  },
);
export const fetchSpecialityAuditAnalysis = createAsyncThunk(
  "workflowDashboard/fetchSpecialityAuditAnalysis",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/code_enhance_dashboard_speciality_audit_analysis",
        payload,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          "Failed to load specialty audit analysis",
      );
    }
  },
);
export const fetchFinancialImpactBySpeciality = createAsyncThunk(
  "workflowDashboard/fetchFinancialImpactBySpeciality",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/code_enhance_dashboard_financial_impact_by_speciality",
        payload,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          "Failed to load financial impact by specialty",
      );
    }
  },
);
// ---------------------------------------------------------------------
// Generic config for every searchable/paginated dropdown filter.
// `mapOption` normalizes each raw item into { label, value }.
// Defaults to identity-string mapping if omitted.
// ---------------------------------------------------------------------
export const FILTER_OPTION_CONFIG = {
  episodeId: {
    endpoint: "/code_enhance_review_workflow_episode_identifier_filter",
    responseKey: "episode_identifiers",
    mapOption: (item) => ({ label: item, value: item }), // plain strings
  },
  healthFund: {
    endpoint: "/code_enhance_review_workflow_health_fund_filters",
    responseKey: "health_funds",
    mapOption: (item) => ({ label: item, value: item }), // plain strings
  },
  speciality: {
    endpoint: "/code_enhance_review_workflow_specialities_filter",
    responseKey: "specialities",
    mapOption: (item) =>
      typeof item === "string" ? { label: item, value: item } : item,
  },
  coder: {
    endpoint: "/code_enhance_review_workflow_coder_name_filter",
    responseKey: "reviewers",
    mapOption: (item) => ({ label: item.username, value: item.username }),
  },
  principalDiagnosis: {
    endpoint: "/code_enhance_review_workflow_principal_diagonsis_filter",
    responseKey: "principal_daignosis_codes",
    mapOption: (item) =>
      typeof item === "string" ? { label: item, value: item } : item,
  },
  drg: {
    endpoint: "/code_enhance_review_workflow_drg_code_filter",
    responseKey: "drg_codes",
    mapOption: (item) =>
      typeof item === "string" ? { label: item, value: item } : item,
  },
  auditRuleCategory: {
    endpoint: "/code_enhance_review_workflow_audit_category_filter",
    responseKey: "categories",
    mapOption: (item) =>
      typeof item === "string" ? { label: item, value: item } : item,
  },
  status: {
    endpoint: "/code_enhance_review_workflow_status_filter",
    responseKey: "statuses",
    mapOption: (item) =>
      typeof item === "string" ? { label: item, value: item } : item,
  },
};

const emptyOptionState = () => ({
  options: [],
  search: "",
  pageNumber: 1,
  pageSize: 10,
  hasMore: true,
  loading: false,
  error: null,
});

// One thunk for all filters — pass filterKey to pick the endpoint/response shape.
export const fetchFilterOptions = createAsyncThunk(
  "workflowDashboard/fetchFilterOptions",
  async (
    { filterKey, search = "", page_number = 1, page_size = 10 },
    { rejectWithValue },
  ) => {
    const config = FILTER_OPTION_CONFIG[filterKey];
    if (!config) {
      return rejectWithValue(`Unknown filter key: ${filterKey}`);
    }
    try {
      const response = await axiosInstance.post(config.endpoint, {
        search,
        page_number,
        page_size,
      });
      return {
        filterKey,
        items: response.data?.[config.responseKey] || [],
        has_more: response.data?.has_more,
        page_number: response.data?.page_number,
        page_size: response.data?.page_size,
        _requestParams: { search, page_number, page_size },
      };
    } catch (error) {
      return rejectWithValue({
        filterKey,
        message:
          error?.response?.data?.message ||
          `Failed to load options for ${filterKey}`,
      });
    }
  },
);

const workflowDashboardSlice = createSlice({
  name: "workflowDashboard",
  initialState: {
    filters: initialFilterPayload,
    data: null,
    loading: false,
    error: null,
    filterOptions: {},
    coderAccuracy: null,
    coderAccuracyLoading: false,
    coderAccuracyError: null,
    codeChanges: null,
    codeChangesLoading: false,
    codeChangesError: null,
    topDrg: null,
    topDrgLoading: false,
    topDrgError: null,
    specialtyAuditOutcomes: null,
    specialtyAuditOutcomesLoading: false,
    specialtyAuditOutcomesError: null,
    financialImpact: null,
    financialImpactLoading: false,
    financialImpactError: null,
    coderPerformanceMetrics: null,
    coderPerformanceMetricsLoading: false,
    coderPerformanceMetricsError: null,
    diagnosisChangesTimeline: null,
    diagnosisChangesTimelineLoading: false,
    diagnosisChangesTimelineError: null,
    procedureChangesTimeline: null,
    procedureChangesTimelineLoading: false,
    procedureChangesTimelineError: null,
    specialityAuditAnalysis: null,
    specialityAuditAnalysisLoading: false,
    specialityAuditAnalysisError: null,
    financialImpactBySpeciality: null,
    financialImpactBySpecialityLoading: false,
    financialImpactBySpecialityError: null,
    //last updated states
    coderAccuracyLastUpdated: null,
    codeChangesLastUpdated: null,
    topDrgLastUpdated: null,
    specialtyAuditOutcomesLastUpdated: null,
    financialImpactLastUpdated: null,
    coderPerformanceMetricsLastUpdated: null,
    diagnosisChangesTimelineLastUpdated: null,
    procedureChangesTimelineLastUpdated: null,
    specialityAuditAnalysisLastUpdated: null,
    financialImpactBySpecialityLastUpdated: null,
  },
  reducers: {
    setDashboardFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetDashboardFilters: (state) => {
      state.filters = initialFilterPayload;
    },
    // call when user types in a given filter's search box
    setFilterOptionSearch: (state, action) => {
      const { filterKey, search } = action.payload;
      if (!state.filterOptions[filterKey]) {
        state.filterOptions[filterKey] = emptyOptionState();
      }
      state.filterOptions[filterKey].search = search;
      state.filterOptions[filterKey].pageNumber = 1;
      state.filterOptions[filterKey].options = [];
      state.filterOptions[filterKey].hasMore = true;
    },
    resetFilterOptions: (state, action) => {
      const filterKey = action.payload;
      state.filterOptions[filterKey] = emptyOptionState();
    },
    addTilePinOptimistic: (state, action) => {
      if (!state.data) return;
      const tileName = action.payload;
      const tiles = state.data.assigned_tiles || [];
      if (!tiles.includes(tileName)) {
        state.data.assigned_tiles = [...tiles, tileName];
      }
    },
    removeTilePinOptimistic: (state, action) => {
      if (!state.data) return;
      const tileName = action.payload;
      state.data.assigned_tiles = (state.data.assigned_tiles || []).filter(
        (t) => t !== tileName,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkflowDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchWorkflowDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Generic filter options (episodeId, healthFund, facility, etc.)
      .addCase(fetchFilterOptions.pending, (state, action) => {
        const { filterKey } = action.meta.arg;
        if (!state.filterOptions[filterKey]) {
          state.filterOptions[filterKey] = emptyOptionState();
        }
        state.filterOptions[filterKey].loading = true;
        state.filterOptions[filterKey].error = null;
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        const {
          filterKey,
          items,
          has_more,
          page_number,
          page_size,
          _requestParams,
        } = action.payload;
        const target = state.filterOptions[filterKey] || emptyOptionState();
        const config = FILTER_OPTION_CONFIG[filterKey];

        // ✅ use the per-filter normalizer, fall back to identity-string mapping
        const mapOption =
          config?.mapOption ||
          ((item) =>
            typeof item === "string" ? { label: item, value: item } : item);

        const newOptions = items.map(mapOption);

        target.options =
          _requestParams.page_number === 1
            ? newOptions
            : [...target.options, ...newOptions];
        target.loading = false;
        target.hasMore = !!has_more;
        target.pageNumber = page_number ?? _requestParams.page_number;
        target.pageSize = page_size ?? _requestParams.page_size;
        target.search = _requestParams.search;

        state.filterOptions[filterKey] = target;
      })
      .addCase(fetchFilterOptions.rejected, (state, action) => {
        const filterKey =
          action.payload?.filterKey || action.meta.arg?.filterKey;
        if (!filterKey) return;
        if (!state.filterOptions[filterKey]) {
          state.filterOptions[filterKey] = emptyOptionState();
        }
        state.filterOptions[filterKey].loading = false;
        state.filterOptions[filterKey].error =
          action.payload?.message || "Failed to load options";
      })
      .addCase(fetchCoderAccuracy.pending, (state) => {
        state.coderAccuracyLoading = true;
        state.coderAccuracyError = null;
      })
      .addCase(fetchCoderAccuracy.fulfilled, (state, action) => {
        state.coderAccuracyLoading = false;
        state.coderAccuracy = action.payload;
        state.coderAccuracyLastUpdated = nowIso();
      })
      .addCase(fetchCoderAccuracy.rejected, (state, action) => {
        state.coderAccuracyLoading = false;
        state.coderAccuracyError = action.payload;
      })
      .addCase(fetchCodeChanges.pending, (state) => {
        state.codeChangesLoading = true;
        state.codeChangesError = null;
      })
      .addCase(fetchCodeChanges.fulfilled, (state, action) => {
        state.codeChangesLoading = false;
        state.codeChanges = action.payload;
        state.codeChangesLastUpdated = nowIso();
      })
      .addCase(fetchCodeChanges.rejected, (state, action) => {
        state.codeChangesLoading = false;
        state.codeChangesError = action.payload;
      })

      .addCase(fetchTopDrg.pending, (state) => {
        state.topDrgLoading = true;
        state.topDrgError = null;
      })
      .addCase(fetchTopDrg.fulfilled, (state, action) => {
        state.topDrgLoading = false;
        state.topDrg = action.payload;
        state.topDrgLastUpdated = nowIso();
      })
      .addCase(fetchTopDrg.rejected, (state, action) => {
        state.topDrgLoading = false;
        state.topDrgError = action.payload;
      })
      .addCase(fetchSpecialtyAuditOutcomes.pending, (state) => {
        state.specialtyAuditOutcomesLoading = true;
        state.specialtyAuditOutcomesError = null;
      })
      .addCase(fetchSpecialtyAuditOutcomes.fulfilled, (state, action) => {
        state.specialtyAuditOutcomesLoading = false;
        state.specialtyAuditOutcomes = action.payload;
        state.specialtyAuditOutcomesLastUpdated = nowIso();
      })
      .addCase(fetchSpecialtyAuditOutcomes.rejected, (state, action) => {
        state.specialtyAuditOutcomesLoading = false;
        state.specialtyAuditOutcomesError = action.payload;
      })
      .addCase(fetchFinancialImpact.pending, (state) => {
        state.financialImpactLoading = true;
        state.financialImpactError = null;
      })
      .addCase(fetchFinancialImpact.fulfilled, (state, action) => {
        state.financialImpactLoading = false;
        state.financialImpact = action.payload;
        state.financialImpactLastUpdated = nowIso();
      })
      .addCase(fetchFinancialImpact.rejected, (state, action) => {
        state.financialImpactLoading = false;
        state.financialImpactError = action.payload;
      })
      .addCase(toggleTilePin.rejected, (state, action) => {
        const { tileName, pin } = action.payload || {};
        if (!tileName || !state.data) return;
        // undo the optimistic change that was applied before the call
        if (pin) {
          state.data.assigned_tiles = (state.data.assigned_tiles || []).filter(
            (t) => t !== tileName,
          );
        } else {
          state.data.assigned_tiles = [
            ...(state.data.assigned_tiles || []),
            tileName,
          ];
        }
      })
      .addCase(fetchCoderPerformanceMetrics.pending, (state) => {
        state.coderPerformanceMetricsLoading = true;
        state.coderPerformanceMetricsError = null;
      })
      .addCase(fetchCoderPerformanceMetrics.fulfilled, (state, action) => {
        state.coderPerformanceMetricsLoading = false;
        state.coderPerformanceMetrics = action.payload;
        state.coderPerformanceMetricsLastUpdated = nowIso();
      })
      .addCase(fetchCoderPerformanceMetrics.rejected, (state, action) => {
        state.coderPerformanceMetricsLoading = false;
        state.coderPerformanceMetricsError = action.payload;
      })
      .addCase(fetchDiagnosisChangesTimeline.pending, (state) => {
        state.diagnosisChangesTimelineLoading = true;
        state.diagnosisChangesTimelineError = null;
      })
      .addCase(fetchDiagnosisChangesTimeline.fulfilled, (state, action) => {
        state.diagnosisChangesTimelineLoading = false;
        state.diagnosisChangesTimeline = action.payload;
        state.diagnosisChangesTimelineLastUpdated = nowIso();
      })
      .addCase(fetchDiagnosisChangesTimeline.rejected, (state, action) => {
        state.diagnosisChangesTimelineLoading = false;
        state.diagnosisChangesTimelineError = action.payload;
      })
      .addCase(fetchProcedureChangesTimeline.pending, (state) => {
        state.procedureChangesTimelineLoading = true;
        state.procedureChangesTimelineError = null;
      })
      .addCase(fetchProcedureChangesTimeline.fulfilled, (state, action) => {
        state.procedureChangesTimelineLoading = false;
        state.procedureChangesTimeline = action.payload;
        state.procedureChangesTimelineLastUpdated = nowIso();
      })
      .addCase(fetchProcedureChangesTimeline.rejected, (state, action) => {
        state.procedureChangesTimelineLoading = false;
        state.procedureChangesTimelineError = action.payload;
      })
      .addCase(fetchSpecialityAuditAnalysis.pending, (state) => {
        state.specialityAuditAnalysisLoading = true;
        state.specialityAuditAnalysisError = null;
      })
      .addCase(fetchSpecialityAuditAnalysis.fulfilled, (state, action) => {
        state.specialityAuditAnalysisLoading = false;
        state.specialityAuditAnalysis = action.payload;
        state.specialityAuditAnalysisLastUpdated = nowIso();
      })
      .addCase(fetchSpecialityAuditAnalysis.rejected, (state, action) => {
        state.specialityAuditAnalysisLoading = false;
        state.specialityAuditAnalysisError = action.payload;
      })
      .addCase(fetchFinancialImpactBySpeciality.pending, (state) => {
        state.financialImpactBySpecialityLoading = true;
        state.financialImpactBySpecialityError = null;
      })
      .addCase(fetchFinancialImpactBySpeciality.fulfilled, (state, action) => {
        state.financialImpactBySpecialityLoading = false;
        state.financialImpactBySpeciality = action.payload;
        state.financialImpactBySpecialityLastUpdated = nowIso();
      })
      .addCase(fetchFinancialImpactBySpeciality.rejected, (state, action) => {
        state.financialImpactBySpecialityLoading = false;
        state.financialImpactBySpecialityError = action.payload;
      });
  },
});
export const {
  setDashboardFilters,
  resetDashboardFilters,
  setFilterOptionSearch,
  resetFilterOptions,
  addTilePinOptimistic,
  removeTilePinOptimistic,
} = workflowDashboardSlice.actions;

export default workflowDashboardSlice.reducer;
